import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Languages, LogOut, Menu, X } from "lucide-react";
import type { MessageKey } from "../i18n/index.js";
import { useI18n } from "../i18n/index.js";
import { useSession } from "../lib/session.js";
import { visibleNav } from "../lib/nav.js";
import { cn } from "../lib/cn.js";

export function AppShell() {
  const { user, logout } = useSession();
  const { locale, setLocale, t } = useI18n();
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();
  if (!user) return null;
  const items = visibleNav(user);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-surface lg:grid lg:grid-cols-[16rem_1fr]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded bg-action-primary focus:px-3 focus:py-2 focus:text-ink"
      >
        {t("common.skipToContent")}
      </a>

      <div className="flex items-center justify-between border-b border-line bg-surface-raised px-4 py-3 lg:hidden">
        <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">
          {t("common.brandName")}
        </p>
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={navOpen}
          aria-controls="primary-navigation"
          onClick={() => setNavOpen((open) => !open)}
        >
          {navOpen ? (
            <X className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Menu className="h-5 w-5" aria-hidden="true" />
          )}
          {navOpen ? t("common.closeMenu") : t("common.openMenu")}
        </Button>
      </div>

      <aside
        id="primary-navigation"
        className={cn(
          "border-line bg-surface-raised lg:block lg:border-e",
          navOpen ? "block border-b" : "hidden"
        )}
        aria-label="Primary"
      >
        <div className="hidden items-center gap-3 border-b border-line px-4 py-4 lg:flex">
          <img
            src="/brand/grizzly-mark.png"
            alt=""
            className="h-10 w-10 object-contain"
          />
          <div>
            <p className="font-display text-sm font-bold uppercase tracking-wide leading-tight text-ink">
              {t("common.brandName")}
            </p>
            <p className="text-xs text-ink-muted">{t("common.brandSubtitle")}</p>
          </div>
        </div>
        <nav aria-label="Primary" className="flex flex-col gap-0.5 p-3">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex min-h-11 items-center gap-3 rounded-[var(--radius-card)] px-3 text-sm font-medium",
                  isActive
                    ? "bg-action-primary/10 text-action-primary-text"
                    : "text-ink-muted hover:bg-surface-inset hover:text-ink"
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3">
          <Badge tone="neutral">{t(`roles.${user.roleKey}` as MessageKey)}</Badge>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              data-testid="locale-toggle"
              aria-label={t("common.switchLanguage")}
              title={t("common.switchLanguage")}
              onClick={() => setLocale(locale === "ar" ? "en" : "ar")}
            >
              <Languages className="h-4 w-4" aria-hidden="true" />
              {locale === "ar" ? "EN" : "عربي"}
            </Button>
            <span className="hidden text-sm text-ink-muted sm:inline">
              {user.displayName}
            </span>
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
              {t("common.signOut")}
            </Button>
          </div>
        </header>
        <main id="main" className="min-w-0 flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
